import { useLocale } from "../../../hooks/useLocale";
import { msg } from "../../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDateTime } from "src/utils/dateTime";
import { Box, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import Spacer from "@ui/spacer/Spacer";
import { ProgressBar } from "@ui/spinner/ProgressBar";
import { Table } from "@ui/table/Table";
import { Typo } from "@ui/text/Typo";
import { useEffect, useRef, useState } from "react";
import { saveAs } from "file-saver";
import { GD } from "src/GD";
import { cents } from "src/tools/tools";
import { IPagination, Pagination } from "../Pagination";
import { NoRecords } from "../../providers/NoRecords";
import { S_DECTA_SESSION_POPUP } from "../popup/DectaSessionPopup";

interface IFraudItem {
	id: number;
	payment_id: number;
	decta_payment_id: number;
	yyyymm: number;
	created_at: string;
	fraud_date: string;
	amount: number;
	acq_req_nr: string;
	type: string;
}

interface IFraudsPagination {
	total: number;
	page: number;
	perPage: number;
	totalPages: number;
}

const PER_PAGE = 20;

const formatDateTime = (value?: string | null) => {
	if (!value) return "—";
	return formatDisplayDateTime(value, { fallback: value });
};

const formatPeriod = (value: number) => {
	const period = String(value);
	return period.length === 6 ? `${period.slice(0, 4)}-${period.slice(4)}` : period;
};

const normalizePagination = (pagination?: IFraudsPagination): IPagination | undefined => {
	if (!pagination) return undefined;
	return {
		total: Number(pagination.total) || 0,
		page: Math.max(0, Number(pagination.page) || 0),
		perPage: Number(pagination.perPage) || PER_PAGE,
		totalPages: Number(pagination.totalPages) || 0,
	};
};

const idSx = {
	fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
	fontVariantNumeric: "tabular-nums",
	whiteSpace: "nowrap",
};

export const Frauds = () => {
	useLocale();
	const [currentPage, setCurrentPage] = useState(0);
	const [exportProgress, setExportProgress] = useState<string | null>(null);
	const [exportError, setExportError] = useState<string | null>(null);
	const exportAbort = useRef<AbortController | null>(null);
	useEffect(() => () => { exportAbort.current?.abort(); }, []);

	const getRequestData = (page: number) => ({
		method: "processing/frauds",
		data: { page, limit: PER_PAGE },
	});

	const [response, reload, _reset, busy] = GD.REQ_API.useRequest(null, getRequestData(currentPage));
	const items: IFraudItem[] = Array.isArray(response?.data?.items) ? response.data.items : [];
	const pagination = normalizePagination(response?.data?.pagination);
	const hasResponse = response !== null && response !== undefined;
	const noData = hasResponse && !busy && !response?.error && items.length === 0;

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
		reload(getRequestData(page));
	};

	const refresh = () => reload(getRequestData(currentPage));

	const downloadCsv = async () => {
		if (exportAbort.current) return;
		const controller = new AbortController();
		exportAbort.current = controller;
		setExportError(null);
		setExportProgress("Downloading…");
		try {
			const rows: IFraudItem[] = [];
			const seen = new Set<string>();
			let total: number | undefined;
			for (let page = 0; ; page++) {
				controller.signal.throwIfAborted();
				const result = await GD.REQ_API.request({ ...getRequestData(page), signal: controller.signal });
				controller.signal.throwIfAborted();
				if (result?.error) throw new Error(result.error.message || msg("couldNotLoadFrauds"));
				if (!Array.isArray(result?.data?.items)) throw new Error(msg("invalidFraudsResponse"));
				const batch: IFraudItem[] = result.data.items;
				const meta = normalizePagination(result.data.pagination);
				if (meta) {
					if (meta.page !== page || (total !== undefined && total !== meta.total)) {
						throw new Error(msg("fraudsListChangedDuringExportPleaseTryAgain"));
					}
					total = meta.total;
				}
				for (const row of batch) {
					const key = String(row.id);
					if (row.id == null || seen.has(key)) throw new Error(msg("repeatedOrInvalidFraudRecordPleaseRestartTheExport"));
					seen.add(key);
					rows.push(row);
				}
				if (meta ? page + 1 >= (meta.totalPages || Math.ceil(meta.total / meta.perPage)) : batch.length < PER_PAGE) break;
				if (batch.length === 0) throw new Error(msg("incompleteFraudsResponsePleaseTryAgain"));
			}
			if (total !== undefined && rows.length !== total) throw new Error(msg("incompleteFraudsExportPleaseTryAgain"));
			const headers = ["fraud_date", "id", "payment_id", "decta_payment_id", "yyyymm", "amount", "acq_req_nr", "type", "created_at"] as const;
			const escapeCell = (value: unknown) => {
				const text = value == null ? "" : String(value);
				return /[,"\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
			};
			const csv = [headers.join(","), ...rows.map(row => headers.map(key => escapeCell(key === "amount" ? cents(row.amount) : row[key])).join(","))].join("\r\n");
			saveAs(new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" }), `frauds_${new Date().toISOString().slice(0, 10)}.csv`);
		} catch (error) {
			if (!controller.signal.aborted) setExportError(error instanceof Error ? error.message : msg("couldNotExportFraudsPleaseTryAgain"));
		} finally {
			if (exportAbort.current === controller) exportAbort.current = null;
			if (!controller.signal.aborted) setExportProgress(null);
		}
	};

	const headerSx = {
		backgroundColor: "#d1dbe6",
		whiteSpace: "nowrap" as const,
	};

	return (
		<VBox sx={{ flex: 1, minWidth: 0 }}>
			<Spacer size="20px" />
			<ProgressBar busy={busy} />

			{response?.error && !busy && (
				<Box sx={{ padding: "20px" }}>
					<Typo variant="medium" sx={{ color: "#DC2626" }}>
						{response.error.message ?? msg("errorLoadingFrauds")}
					</Typo>
				</Box>
			)}

			{noData && <NoRecords line1={msg("ui_36f41290f6")} line2={msg("ui_2aeccae289")} />}

			{!busy && !response?.error && items.length > 0 && (
				<Box sx={{ overflowX: "auto", minWidth: 0 }}>
					<Table<IFraudItem>
						data={items}
						onRowClick={row => S_DECTA_SESSION_POPUP.invoke({ sessionId: String(row.payment_id) })}
						order={["fraud_date", "id", "payment_id", "decta_payment_id", "yyyymm", "amount", "acq_req_nr", "type", "created_at"]}
						headers={[
							{ value: "Fraud date", gridColumn: "max-content", sx: headerSx },
							{ value: "ID", gridColumn: "max-content", sx: headerSx },
							{ value: "Payment ID", gridColumn: "max-content", sx: headerSx },
							{ value: "Decta payment ID", gridColumn: "max-content", sx: headerSx },
							{ value: "Period", gridColumn: "max-content", sx: headerSx },
							{ value: "Amount", gridColumn: "max-content", sx: { ...headerSx, textAlign: "right" } },
							{ value: "Acquirer request №", gridColumn: "minmax(220px, 1fr)", sx: headerSx },
							{ value: "Type", gridColumn: "minmax(190px, 1fr)", sx: headerSx },
							{ value: "Created", gridColumn: "max-content", sx: headerSx },
						]}
						gridTemplateColumns="max-content max-content max-content max-content max-content max-content minmax(220px, 1fr) minmax(190px, 1fr) max-content"
						sx={{ minWidth: "max-content", backgroundColor: "#f8fafc" }}
						cellPadding="10px 18px"
						headPadding="10px 16px"
						cellFactory={(row, cell, _index, _key, name) => {
							if (name === "Fraud date") return { content: <Typo variant="medium"><DateTimeTooltip value={row.fraud_date}>{formatDateTime(row.fraud_date)}</DateTimeTooltip></Typo> };
							if (name === "Created") return { content: <Typo variant="medium"><DateTimeTooltip value={row.created_at}>{formatDateTime(row.created_at)}</DateTimeTooltip></Typo> };
							if (name === "Period") return { content: <Typo variant="medium" sx={idSx}>{formatPeriod(row.yyyymm)}</Typo> };
							if (name === "Amount") {
								return {
									sx: { textAlign: "right" },
									content: <Typo variant="medium" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{cents(row.amount)}</Typo>,
								};
							}
							if (name === "Type") return { content: <Typo variant="medium" sx={{ whiteSpace: "nowrap" }}>{row.type || "—"}</Typo> };
							if (name === "Acquirer request №") return { content: <Typo variant="medium" sx={idSx}>{row.acq_req_nr || "—"}</Typo> };
							if (name === "ID" || name === "Payment ID" || name === "Decta payment ID") {
								return { content: <Typo variant="medium" sx={idSx}>{cell != null ? String(cell) : "—"}</Typo> };
							}
							return { content: cell != null ? String(cell) : "—" };
						}}
					/>
				</Box>
			)}

			<Box
				sx={{
					display: "flex",
					gap: "10px",
					alignItems: "center",
					flexWrap: "wrap",
					position: "sticky",
					bottom: 0,
					zIndex: 2,
					backgroundColor: "#f8fafc",
					padding: "10px 0",
				}}>
				<Pagination pagination={pagination} onPageChange={handlePageChange} disabled={exportProgress !== null} />
				<Spacer grow />
				{exportError && <Typo variant="medium" sx={{ color: "#DC2626" }}><span role="alert">{exportError}</span></Typo>}
				<Button onClick={downloadCsv} title={exportProgress ?? msg("downloadCsv")} variant="secondarySmall" disabled={busy || exportProgress !== null || !!response?.error || items.length === 0} />
				<Button onClick={refresh} title={msg("refresh")} variant="primarySmall" disabled={busy || exportProgress !== null} />
			</Box>
		</VBox>
	);
};
